/**
 * Announcements from the team (TMMAPP-0142 U2 item 13a; TMMAPI-0181 U5 item 32a): free, no AI, and
 * read by every person, a guest included. Wire types from `src/api/generated/`, normalized into the
 * module's domain types.
 *
 * - **The list:** pinned first, then newest; `unreadCount`; `popupId`, the oldest unread pop-up,
 *   shown once and then marked read.
 * - **Read:** `POST …/{id}/read` (idempotent, `204`).
 * - The body is the help library's Markdown subset, the product name already substituted; an image
 *   in it shows as its alt text.
 */
import { request } from './client';
import type {
  MemberAnnouncement as WireAnnouncement,
  MemberAnnouncementList as WireAnnouncementList,
} from './generated';

export type AnnouncementKind = 'NEWS' | 'FEATURE' | 'MAINTENANCE' | 'IMPORTANT';

const KINDS: ReadonlySet<string> = new Set(['NEWS', 'FEATURE', 'MAINTENANCE', 'IMPORTANT']);

export interface Announcement {
  id: number;
  /** A kind this build doesn't know reads as `NEWS`. */
  kind: AnnouncementKind;
  title: string;
  body: string;
  pinned: boolean;
  popup: boolean;
  /** When it was published (an instant). */
  publishAt: string;
  read: boolean;
}

export interface AnnouncementList {
  items: Announcement[];
  unreadCount: number;
  /** The announcement to show once as a pop-up; null when none. */
  popupId: number | null;
}

export function normalizeAnnouncement(w: WireAnnouncement): Announcement {
  return {
    id: w.id,
    kind: KINDS.has(w.kind) ? w.kind : 'NEWS',
    title: w.title,
    body: w.body,
    pinned: w.pinned,
    popup: w.popup,
    publishAt: w.publishAt,
    read: w.read,
  };
}

export function normalizeAnnouncementList(w: WireAnnouncementList): AnnouncementList {
  return {
    items: w.announcements.map(normalizeAnnouncement),
    unreadCount: Math.max(0, w.unreadCount),
    popupId: w.popupId ?? null,
  };
}

const BASE = '/v1/me/announcements';

export const announcementsApi = {
  async list(): Promise<AnnouncementList> {
    return normalizeAnnouncementList(await request<WireAnnouncementList>('GET', BASE));
  },
  async get(id: number): Promise<Announcement> {
    return normalizeAnnouncement(await request<WireAnnouncement>('GET', `${BASE}/${id}`));
  },
  /** POST …/{id}/read — idempotent. */
  async markRead(id: number): Promise<void> {
    await request<unknown>('POST', `${BASE}/${id}/read`);
  },
};
